import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Plus, Trash, ArrowSquareOut, UploadSimple, FileText, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StatusBadge } from "./Semaforo";
import { formatDateTime } from "../lib/format";
import { useConfirm } from "./ConfirmDialog";


// ----------------------------------------------------------------
// LinksEditor: edit primary vinculación (community/authority) +
// optional project_id "parent" (only for commitments).
// ----------------------------------------------------------------
export function LinksEditor({
  related,        // { type, id, name, entity_type? } | null
  project,        // { id, name } | null   (optional, only commitments)
  onChange,       // ({related_entity_type, related_entity_id, project_id}) => Promise
  showProject = false,
}) {
  const confirm = useConfirm();
  const [communities, setCommunities] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [type, setType] = useState(related?.type === "community" ? "community" : related?.type === "authority" ? "authority" : "");
  const [entityId, setEntityId] = useState(related?.id || "");
  const [projectId, setProjectId] = useState(project?.id || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/communities").then((r) => setCommunities(r.data));
    api.get("/authorities").then((r) => setAuthorities(r.data));
    if (showProject) api.get("/projects").then((r) => setProjects(r.data));
  }, [showProject]);

  // Sync when parent reloads
  useEffect(() => {
    setType(related?.type === "community" ? "community" : related?.type === "authority" ? "authority" : "");
    setEntityId(related?.id || "");
    setProjectId(project?.id || "");
  }, [related?.id, related?.type, project?.id]);

  const apply = async () => {
    try {
      setSaving(true);
      await onChange({
        related_entity_type: type || null,
        related_entity_id: type ? entityId || null : null,
        project_id: showProject ? projectId || null : undefined,
      });
      toast.success("Vinculación actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const clear = async () => {
    if (!(await confirm({ title: "Eliminar vinculación", description: "¿Deseas eliminar la vinculación principal?", danger: true }))) return;
    try {
      setSaving(true);
      setType(""); setEntityId("");
      await onChange({ related_entity_type: null, related_entity_id: null });
      toast.success("Vinculación eliminada");
    } finally { setSaving(false); }
  };

  const clearProject = async () => {
    if (!(await confirm({ title: "Quitar proyecto madre", description: "¿Deseas quitar el proyecto madre asociado?", danger: true }))) return;
    setProjectId("");
    await onChange({ project_id: null });
    toast.success("Proyecto madre quitado");
  };

  const list = type === "authority" ? authorities : type === "community" ? communities : [];

  return (
    <Card className="rounded-md mt-4">
      <CardHeader><CardTitle className="text-base">Vinculaciones</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {/* Current primary link */}
        {related ? (
          <div className="rounded-md border border-slate-200 px-3 py-2 flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-slate-500">Vinculado a</span>
            <StatusBadge tone="info">{related.type === "community" ? "Comunidad" : "Autoridad"}</StatusBadge>
            <Link className="font-medium hover:underline flex items-center gap-1" to={relatedTo(related)}>
              {related.name} <ArrowSquareOut size={12} />
            </Link>
            <Button size="sm" variant="ghost" onClick={clear} className="ml-auto" data-testid="clear-link-btn">
              <Trash size={14} className="text-red-600" />
            </Button>
          </div>
        ) : (
          <div className="text-xs text-slate-500 italic">Sin vinculación principal.</div>
        )}

        {/* Editor */}
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-3">
            <Label className="text-xs">Tipo</Label>
            <Select value={type || "__none__"} onValueChange={(v) => { setType(v === "__none__" ? "" : v); setEntityId(""); }}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                <SelectItem value="community">Comunidad</SelectItem>
                <SelectItem value="authority">Autoridad</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-7">
            <Label className="text-xs">Entidad</Label>
            <Select value={entityId || "__none__"} onValueChange={(v) => setEntityId(v === "__none__" ? "" : v)} disabled={!type}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {list.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Button onClick={apply} className="bg-slate-900 w-full" disabled={saving || !type || !entityId} data-testid="save-link-btn">
              <Plus size={14} className="mr-1" /> Vincular
            </Button>
          </div>
        </div>

        {/* Project parent (only for commitments) */}
        {showProject && (
          <div className="pt-3 border-t border-slate-200 space-y-2">
            <Label className="text-xs">Proyecto madre</Label>
            <div className="flex items-center gap-2">
              <Select value={projectId || "__none__"} onValueChange={(v) => setProjectId(v === "__none__" ? "" : v)}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin proyecto madre —</SelectItem>
                  {projects.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={() => onChange({ project_id: projectId || null }).then(() => toast.success("Proyecto actualizado"))} className="bg-slate-900">
                Guardar
              </Button>
              {project && <Button variant="ghost" onClick={clearProject}><Trash size={14} className="text-red-600" /></Button>}
            </div>
            {project && (
              <Link to={`/proyectos/${project.id}`} className="inline-flex items-center gap-1 text-sm text-slate-700 hover:underline">
                {project.name} <ArrowSquareOut size={12} />
              </Link>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ----------------------------------------------------------------
// EvidenceUploader: upload, list, delete documents tied to entity.
// Includes mandatory title prompt for photos + lightbox preview.
// ----------------------------------------------------------------
export function EvidenceUploader({ entityType, entityId, documents, onReload }) {
  const confirm = useConfirm();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingTitle, setPendingTitle] = useState("");
  const [preview, setPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    setPendingTitle(f.name.replace(/\.[^.]+$/, ""));
    if (fileRef.current) fileRef.current.value = "";
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("related_entity_type", entityType);
      fd.append("related_entity_id", entityId);
      if (pendingTitle.trim()) fd.append("description", pendingTitle.trim());
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidencia subida");
      setPendingFile(null);
      setPendingTitle("");
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir archivo");
    } finally { setUploading(false); }
  };

  const cancelUpload = () => { setPendingFile(null); setPendingTitle(""); };

  const remove = async (id) => {
    if (!(await confirm({ title: "Eliminar archivo", description: "¿Deseas eliminar este archivo? Esta acción no se puede deshacer.", danger: true }))) return;
    await api.delete(`/files/${id}`);
    if (preview?.id === id) setPreview(null);
    await onReload();
  };

  const startEditTitle = (d) => { setEditingId(d.id); setEditingTitle(d.description || ""); };
  const saveTitle = async (fid) => {
    try {
      await api.patch(`/files/${fid}`, { description: editingTitle.trim() });
      toast.success("Título actualizado");
      setEditingId(null);
      await onReload();
    } catch (err) { toast.error("No se pudo guardar el título"); }
  };

  const isImage = (ct) => ct && ct.startsWith("image/");
  const downloadUrl = (d) => `${API}/files/${d.id}/download?auth=${encodeURIComponent(localStorage.getItem("sigsaop_token") || "")}`;
  const photos = documents.filter((d) => isImage(d.content_type));
  const files = documents.filter((d) => !isImage(d.content_type));

  return (
    <>
      <Card className="rounded-md mt-4">
        <CardHeader><CardTitle className="text-base">Evidencias y documentos</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-dashed border-slate-300 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <UploadSimple size={18} className="text-slate-500" />
              <Label className="text-sm font-medium">Subir nueva evidencia</Label>
            </div>
            <Input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
              onChange={onPickFile}
              disabled={uploading || !!pendingFile}
              data-testid="file-input"
            />
            <p className="text-[11px] text-slate-500">Formatos aceptados: imágenes, PDF, Word, Excel, PowerPoint, texto. Máx. 25 MB.</p>
          </div>

          {pendingFile && (
            <div className="rounded-md border border-brand-green-300 bg-brand-green-50/50 p-3 flex items-start gap-3">
              {isImage(pendingFile.type) ? (
                <img src={URL.createObjectURL(pendingFile)} alt="preview" className="h-16 w-16 object-cover rounded border" />
              ) : (
                <FileText size={32} className="text-slate-400" />
              )}
              <div className="flex-1 space-y-2">
                <Label className="text-xs font-medium text-slate-700">Título / descripción de la evidencia</Label>
                <Input
                  autoFocus
                  value={pendingTitle}
                  onChange={(e) => setPendingTitle(e.target.value)}
                  placeholder="Ej: Minuta firmada, foto asistentes, certificado, factura…"
                />
                <div className="flex items-center gap-2">
                  <Button size="sm" className="bg-slate-900 text-white" onClick={confirmUpload} disabled={uploading || !pendingTitle.trim()}>
                    {uploading ? "Subiendo…" : "Subir"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelUpload} disabled={uploading}>Cancelar</Button>
                </div>
              </div>
            </div>
          )}

          {/* Photo grid */}
          {photos.length > 0 && (
            <div>
              <div className="text-xs font-medium text-slate-600 mb-2">Fotografías ({photos.length})</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {photos.map((d) => (
                  <div key={d.id} className="group rounded-md overflow-hidden border border-slate-200 bg-white hover:shadow-md transition">
                    <button type="button" onClick={() => setPreview(d)} className="block w-full aspect-square bg-slate-50 overflow-hidden">
                      <img src={downloadUrl(d)} alt={d.description || d.original_filename} className="h-full w-full object-cover transition group-hover:scale-105" />
                    </button>
                    <div className="px-2 py-1.5 flex items-center gap-1">
                      <span className="flex-1 text-[11px] text-slate-700 truncate" title={d.description || d.original_filename}>{d.description || d.original_filename}</span>
                      <button type="button" onClick={() => remove(d.id)} className="text-red-500 hover:text-red-700"><Trash size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Non-image files */}
          {files.length > 0 && (
            <div>
              <div className="text-xs font-medium text-slate-600 mb-2">Documentos ({files.length})</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Archivo</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Subido</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>
                        <a href={downloadUrl(d)} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline">
                          <FileText size={20} className="text-slate-400" />
                          <span className="font-medium">{d.original_filename}</span>
                        </a>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{d.content_type}</TableCell>
                      <TableCell className="text-xs">{d.description || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-500">{formatDateTime(d.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => remove(d.id)} data-testid={`del-file-${d.id}`}>
                          <Trash size={14} className="text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {documents.length === 0 && !pendingFile && (
            <div className="text-sm text-slate-500 italic">Sin documentos.</div>
          )}
        </CardContent>
      </Card>

      {/* LIGHTBOX */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <div className="max-w-5xl w-full max-h-[90vh] flex flex-col items-stretch gap-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between text-white">
              {editingId === preview.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input autoFocus value={editingTitle} onChange={(e) => setEditingTitle(e.target.value)} className="bg-white text-slate-900 max-w-md" />
                  <Button size="sm" className="bg-brand-green-600" onClick={() => saveTitle(preview.id)}>Guardar</Button>
                  <Button size="sm" variant="ghost" className="text-white" onClick={() => setEditingId(null)}>Cancelar</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-1">
                  <h3 className="font-medium text-base truncate" title={preview.description || preview.original_filename}>
                    {preview.description || preview.original_filename}
                  </h3>
                  <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => startEditTitle(preview)}>✎</Button>
                </div>
              )}
              <button type="button" onClick={() => setPreview(null)} className="text-white hover:bg-white/10 rounded p-2 ml-2" aria-label="Cerrar">✕</button>
            </div>
            <img src={downloadUrl(preview)} alt={preview.description || preview.original_filename} className="max-h-[80vh] w-full object-contain rounded shadow-lg bg-black" />
          </div>
        </div>
      )}
    </>
  );
}


export function relatedTo(r) {
  if (r.type === "community") return `/comunidades/${r.id}`;
  const etype = r.entity_type;
  const route = etype === "gobierno" ? "autoridades-gobierno"
              : etype === "parlamentario" ? "parlamentarios"
              : etype === "gore" ? "autoridades-gore"
              : etype === "organizacion" ? "organizaciones"
              : "autoridades-comunales";
  return `/${route}/${r.id}`;
}


// Small inline label with optional green check when the tab has content.
// Usage:  <TabsTrigger value="x"><TabLabel label="Resumen" hasData /></TabsTrigger>
export function TabLabel({ label, hasData }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {label}
      {hasData ? (
        <CheckCircle size={14} weight="fill" className="text-brand-green-500" data-testid="tab-has-data" />
      ) : null}
    </span>
  );
}

